import { Link } from 'react-router-dom';

import {
  allNavItems,
  availabilityLabel,
  isNavItemEnabled,
  visibleNavGroups,
  type NavAvailability,
} from './navigation';
import { StatusBadge, type StatusTone } from './StatusBadge';
import { InfoHint } from './components/InfoHint';
import { InboxPanel } from './InboxPanel';
import { ME_PERMISSIONS_PATH, describePermissionsState } from '../lib/permissions/client';
import type { PermissionsState } from '../lib/permissions/types';

export interface HomePageProps {
  readonly permissions: PermissionsState;
  readonly apiBaseUrl: string;
}

function availabilityTone(availability: NavAvailability): StatusTone {
  switch (availability) {
    case 'available':
      return 'success';
    case 'planned':
      return 'planned';
    case 'phase-2':
      return 'neutral';
  }
}

/**
 * ภาพรวมของ "สิ่งที่ผู้ใช้คนนี้เข้าถึงได้" ไม่ใช่แคตตาล็อกทั้งระบบ
 *
 * ตารางความพร้อมจึงถูกขับด้วย `visibleNavGroups` ชุดเดียวกับเมนู — ถ้าปล่อยให้
 * ตารางลิสต์ทุกโมดูลไว้เหมือนเดิม ผู้ที่ถูกซ่อนเมนูไปแล้ว (เช่น ADMIN ที่ห้ามแตะ
 * ข้อมูลธุรกิจตาม D-009) ก็ยังอ่านชื่อโมดูลธุรกิจทั้งหมดได้จากหน้าแรกอยู่ดี
 */
export function HomePage({ permissions, apiBaseUrl }: HomePageProps) {
  const groups = visibleNavGroups(permissions);
  const items = groups.flatMap((group) => group.items);
  const readyCount = items.filter(isNavItemEnabled).length;
  const plannedCount = items.filter((item) => item.availability === 'planned').length;
  const phase2Count = items.filter((item) => item.availability === 'phase-2').length;
  const totalCount = allNavItems().length;
  const verified = permissions.status === 'ready';

  return (
    <div className="cap-page">
      <div className="cap-page__head">
        <div>
          <h1 className="cap-page__title">
            ภาพรวมระบบ{' '}
            <InfoHint
              label="เกี่ยวกับหน้าภาพรวมระบบ"
              text="CAP ERP เฟส 1 — โครงหน้าจอ Desktop และเครื่องสแกนหน้างาน ยังไม่มีธุรกรรมจริง"
            />
          </h1>
        </div>
        <div className="cap-page__actions">
          <Link className="cap-button cap-button--ghost" to="/scanner">
            เปิดมุมมองเครื่องสแกน
          </Link>
        </div>
      </div>

      {!verified ? (
        <div className="cap-banner cap-banner--warning" role="status" data-testid="deny-safe-banner">
          <strong>ปิดคำสั่งทั้งหมดไว้ก่อน (deny-safe)</strong>
          <span>{describePermissionsState(permissions)}</span>
          <span className="cap-banner__note">
            การซ่อน/ปิดปุ่มที่หน้าจอเป็นเพียง UX — การบังคับสิทธิ์จริงอยู่ที่ backend
            เสมอ
          </span>
        </div>
      ) : null}

      {verified ? <InboxPanel apiBaseUrl={apiBaseUrl} /> : null}

      <section className="cap-cards" aria-label="สรุปสถานะระบบ">
        <article className="cap-card cap-card--stat">
          <div className="cap-card__label">หน้าจอที่คุณเปิดใช้งานได้</div>
          <div className="cap-card__value" data-testid="visible-item-count">
            {readyCount}
            <span className="cap-card__unit"> / {items.length}</span>
          </div>
          <div className="cap-card__note">
            รอ implement {plannedCount} รายการ · นอกขอบเขตเฟส 1 อีก {phase2Count} รายการ ·
            เมนูทั้งระบบ {totalCount} รายการ
          </div>
        </article>

        <article className="cap-card cap-card--stat">
          <div className="cap-card__label">โหมดเติมเต็มที่ใช้งานจริง</div>
          <div className="cap-card__value">PUSH</div>
          <div className="cap-card__note">
            Phase 1 ไม่ปล่อยสัญญาณ Pull/Kanban ทุกคำสั่งที่เกี่ยวข้องถูกปิดไว้ (D-034)
          </div>
        </article>

        <article className="cap-card cap-card--stat">
          <div className="cap-card__label">สถานะการยืนยันสิทธิ์</div>
          <div className="cap-card__value cap-card__value--sm">
            <StatusBadge tone={verified ? 'success' : 'warning'}>
              {verified ? 'ยืนยันแล้ว' : 'ยังไม่ยืนยัน'}
            </StatusBadge>
          </div>
          <div className="cap-card__note">{describePermissionsState(permissions)}</div>
        </article>

        <article className="cap-card cap-card--stat">
          <div className="cap-card__label">ปลายทาง API สิทธิ์</div>
          <div className="cap-card__value cap-card__value--code" data-testid="permissions-endpoint">
            {`${apiBaseUrl}${ME_PERMISSIONS_PATH}`}
          </div>
          <div className="cap-card__note">
            same-origin เท่านั้น — ค่า cross-origin ถูกปฏิเสธโดยอัตโนมัติ
          </div>
        </article>
      </section>

      <section className="cap-card cap-card--table" aria-label="ความพร้อมของโมดูลเฟส 1">
        <div className="cap-card__head">
          <h2 className="cap-card__title">
            ความพร้อมของโมดูลเฟส 1{' '}
            <InfoHint
              label="เกี่ยวกับตารางความพร้อมของโมดูล"
              text="อ้างอิง docs/TASKS.md — แสดงเฉพาะรายการที่สิทธิ์ของคุณเข้าถึงได้"
            />
          </h2>
        </div>
        <div className="cap-table-wrap">
          <table className="cap-table">
            <caption className="cap-visually-hidden">
              รายการโมดูลเฟส 1 ที่คุณมีสิทธิ์เข้าถึง พร้อมรหัสสิทธิ์และสถานะความพร้อม
            </caption>
            <thead>
              <tr>
                <th scope="col">กลุ่ม</th>
                <th scope="col">โมดูล / หน้าจอ</th>
                <th scope="col">รหัสสิทธิ์</th>
                <th scope="col">Action</th>
                <th scope="col">สถานะ</th>
                <th scope="col">หมายเหตุ</th>
              </tr>
            </thead>
            <tbody>
              {items.length === 0 ? (
                <tr data-testid="module-rows-empty">
                  <td className="cap-table__muted" colSpan={6}>
                    ไม่มีโมดูลที่สิทธิ์ปัจจุบันเข้าถึงได้
                  </td>
                </tr>
              ) : null}
              {groups.flatMap((group) =>
                group.items.map((item) => (
                  <tr key={item.id} data-testid={`module-row-${item.id}`}>
                    <td className="cap-table__muted">{group.title}</td>
                    <th scope="row" className="cap-table__row-head">
                      {item.label}
                    </th>
                    <td className="cap-table__code">{item.moduleCode ?? '—'}</td>
                    <td className="cap-table__code">{item.action ?? '—'}</td>
                    <td>
                      <StatusBadge tone={availabilityTone(item.availability)}>
                        {availabilityLabel(item.availability)}
                      </StatusBadge>
                    </td>
                    <td className="cap-table__muted">{item.note ?? '—'}</td>
                  </tr>
                )),
              )}
            </tbody>
          </table>
        </div>
        <div className="cap-card__foot">
          แสดง {items.length} จาก {totalCount} รายการ · ข้อมูลชุดนี้มาจากโครงเมนูของแอป
          ไม่ใช่ข้อมูลธุรกิจจริง
        </div>
      </section>
    </div>
  );
}
